/**
 * Particle system — ASCII particle effects for atmospheric animation.
 *
 * TypeScript port of the Rust dd-ascii-anim particle module. Provides emitters
 * that spawn, update, and render character-based particles onto a FrameBuffer.
 * Built-in templates cover common effects: rain, fire, magic sparkles, and snow.
 *
 * Each Particle has position, velocity, a glyph that cycles as it ages, and a
 * finite lifetime. The ParticleEmitter manages the full lifecycle: spawning at
 * a configured rate, advancing physics, removing dead particles, and rendering
 * survivors to a buffer.
 *
 * No box-drawing characters are used -- only pure alphanumeric/symbol glyphs.
 */

import { Color, FrameBuffer } from "./engine";

// ---------------------------------------------------------------------------
// Particle
// ---------------------------------------------------------------------------

/** A single particle with position, velocity, and lifecycle. */
export interface Particle {
  /** Horizontal position (fractional for sub-cell movement). */
  x: number;
  /** Vertical position (fractional for sub-cell movement). */
  y: number;
  /** Horizontal velocity (cells per update). */
  vx: number;
  /** Vertical velocity (cells per update). */
  vy: number;
  /** The current display glyph. */
  glyph: string;
  /** Total lifetime in update ticks before this particle dies. */
  lifetime: number;
  /** How many update ticks this particle has lived. */
  age: number;
}

/** Whether a particle has exceeded its lifetime. */
function isParticleDead(p: Particle): boolean {
  return p.age >= p.lifetime;
}

// ---------------------------------------------------------------------------
// ParticleTemplate
// ---------------------------------------------------------------------------

/**
 * Template defining how particles behave.
 *
 * The template controls the glyph sequence (which cycles as the particle
 * ages), initial velocity with random variance, gravity, and lifetime range.
 */
export interface ParticleTemplate {
  /**
   * Glyphs that cycle as the particle ages. The particle selects a glyph
   * based on the ratio of age/lifetime mapped to this sequence.
   */
  glyphSequence: string[];
  /** Base initial velocity [vx, vy]. */
  initialVelocity: [number, number];
  /**
   * Random spread applied to the initial velocity [vx_range, vy_range].
   * Actual velocity = initialVelocity + uniform(-variance, +variance).
   */
  velocityVariance: [number, number];
  /** Gravity applied each update tick. Positive values pull downward. */
  gravity: number;
  /** Range of lifetimes [min, max] assigned randomly at spawn. */
  lifetimeRange: [number, number];
  /** Foreground color for rendered particles. */
  fg: Color;
  /** Background color for rendered particles. */
  bg: Color;
}

/** Rain effect: `|` falling down with slight horizontal drift. */
export function rainTemplate(): ParticleTemplate {
  return {
    glyphSequence: ["|", "|", "|", "."],
    initialVelocity: [0.0, 1.0],
    velocityVariance: [0.3, 0.2],
    gravity: 0.05,
    lifetimeRange: [10, 25],
    fg: new Color(100, 100, 255),
    bg: Color.black(),
  };
}

/** Fire effect: `@#*^.` rising upward with horizontal oscillation. */
export function fireTemplate(): ParticleTemplate {
  return {
    glyphSequence: ["@", "#", "*", "^", "."],
    initialVelocity: [0.0, -0.8],
    velocityVariance: [0.5, 0.3],
    gravity: -0.02,
    lifetimeRange: [8, 20],
    fg: new Color(255, 140, 0),
    bg: Color.black(),
  };
}

/** Magic sparkle effect: `O o * + ~ .` expanding outward from center. */
export function magicTemplate(): ParticleTemplate {
  return {
    glyphSequence: ["O", "o", "*", "+", "~", "."],
    initialVelocity: [0.0, 0.0],
    velocityVariance: [1.0, 1.0],
    gravity: 0.0,
    lifetimeRange: [12, 30],
    fg: new Color(200, 100, 255),
    bg: Color.black(),
  };
}

/** Snow effect: `* . ,` falling slowly with horizontal drift. */
export function snowTemplate(): ParticleTemplate {
  return {
    glyphSequence: ["*", ".", ","],
    initialVelocity: [0.0, 0.3],
    velocityVariance: [0.4, 0.1],
    gravity: 0.01,
    lifetimeRange: [20, 50],
    fg: Color.white(),
    bg: Color.black(),
  };
}

// ---------------------------------------------------------------------------
// Rng — xorshift64 PRNG
// ---------------------------------------------------------------------------

/**
 * Simple deterministic PRNG (xorshift64) for particle spawning.
 *
 * Provides reproducible results for testing while being fast enough
 * for real-time animation. Uses BigInt for 64-bit precision.
 */
class Rng {
  private state: bigint;

  constructor(seed: number) {
    // Avoid zero state which would produce all zeros.
    this.state = seed === 0 ? 1n : BigInt(seed) & 0xffffffffffffffffn;
  }

  /** Generate the next pseudo-random u64 (as bigint). */
  private nextU64(): bigint {
    let x = this.state;
    x ^= (x << 13n) & 0xffffffffffffffffn;
    x ^= x >> 7n;
    x ^= (x << 17n) & 0xffffffffffffffffn;
    this.state = x;
    return x;
  }

  /** Generate a value in [0, max). Returns 0 if max is 0. */
  nextBounded(max: number): number {
    if (max === 0) return 0;
    const val = this.nextU64() % BigInt(max);
    return Number(val);
  }

  /**
   * Generate a float in [-1.0, 1.0).
   *
   * Maps the full u64 range to the interval [-1, 1) for velocity variance.
   */
  nextSignedFloat(): number {
    const val = this.nextU64();
    const normalized = (Number(val) / Number(0xffffffffffffffffn)) * 2.0 - 1.0;
    return normalized;
  }
}

// ---------------------------------------------------------------------------
// ParticleEmitter
// ---------------------------------------------------------------------------

/**
 * Emitter that spawns and manages particles.
 *
 * The emitter maintains a pool of living particles and handles spawn
 * accumulation (fractional spawn rates), physics updates, lifecycle
 * management, and rendering to a FrameBuffer.
 */
export class ParticleEmitter {
  /** Living particles. */
  private _particles: Particle[] = [];
  /** Template for spawning new particles. */
  private template: ParticleTemplate;
  /** Particles to spawn per update tick. */
  private spawnRate: number;
  /** Fractional spawn accumulator (handles sub-integer rates). */
  private spawnAccumulator = 0;
  /** Horizontal spawn area [minX, maxX]. */
  private spawnXRange: [number, number];
  /** Vertical spawn area [minY, maxY]. */
  private spawnYRange: [number, number];
  /** Whether the emitter is actively spawning. */
  private _active = true;
  /** Deterministic PRNG. */
  private rng: Rng;

  /**
   * Create a new particle emitter.
   *
   * @param template  - Defines particle behavior (glyphs, velocity, gravity, lifetime).
   * @param spawnRate - Particles to spawn per update tick (fractional OK).
   * @param spawnXRange - Horizontal spawn area [min, max].
   * @param spawnYRange - Vertical spawn area [min, max].
   * @param seed - PRNG seed for deterministic spawning.
   */
  constructor(
    template: ParticleTemplate,
    spawnRate: number,
    spawnXRange: [number, number],
    spawnYRange: [number, number],
    seed: number,
  ) {
    this.template = template;
    this.spawnRate = spawnRate;
    this.spawnXRange = spawnXRange;
    this.spawnYRange = spawnYRange;
    this.rng = new Rng(seed);
  }

  /**
   * Spawn new particles, advance positions, age particles, and remove dead ones.
   *
   * This is the main update loop. Call once per animation tick.
   */
  update(): void {
    // Spawn new particles based on rate + accumulator
    if (this._active) {
      this.spawnAccumulator += this.spawnRate;
      const toSpawn = Math.floor(this.spawnAccumulator);
      this.spawnAccumulator -= toSpawn;

      for (let i = 0; i < toSpawn; i++) {
        this.spawnParticle();
      }
    }

    // Update existing particles
    for (const particle of this._particles) {
      // Apply velocity
      particle.x += particle.vx;
      particle.y += particle.vy;

      // Apply gravity
      particle.vy += this.template.gravity;

      // Age the particle
      particle.age += 1;

      // Update glyph based on age ratio
      const seqLen = this.template.glyphSequence.length;
      if (seqLen > 0) {
        const ratio = particle.age / particle.lifetime;
        const idx = Math.min(Math.floor(ratio * seqLen), seqLen - 1);
        particle.glyph = this.template.glyphSequence[idx];
      }
    }

    // Remove dead particles
    this._particles = this._particles.filter((p) => !isParticleDead(p));
  }

  /** Spawn a single particle using the template and RNG. */
  private spawnParticle(): void {
    const x = this.randomInRange(this.spawnXRange[0], this.spawnXRange[1]);
    const y = this.randomInRange(this.spawnYRange[0], this.spawnYRange[1]);

    const vx =
      this.template.initialVelocity[0] +
      this.rng.nextSignedFloat() * this.template.velocityVariance[0];
    const vy =
      this.template.initialVelocity[1] +
      this.rng.nextSignedFloat() * this.template.velocityVariance[1];

    const [minLife, maxLife] = this.template.lifetimeRange;
    const lifeRange = Math.max(maxLife - minLife, 1);
    const lifetime = minLife + this.rng.nextBounded(lifeRange);

    const glyph = this.template.glyphSequence.length > 0 ? this.template.glyphSequence[0] : ".";

    this._particles.push({
      x,
      y,
      vx,
      vy,
      glyph,
      lifetime,
      age: 0,
    });
  }

  /** Generate a random float in [min, max). */
  private randomInRange(min: number, max: number): number {
    const t = (this.rng.nextSignedFloat() + 1.0) / 2.0; // [0, 1)
    return min + t * (max - min);
  }

  /**
   * Render all living particles onto a frame buffer.
   *
   * Each particle is written to its integer-rounded position. Particles
   * outside the buffer bounds are silently skipped (they still exist and
   * move, but are not visible).
   */
  render(buffer: FrameBuffer): void {
    const [w, h] = buffer.dimensions();
    for (const particle of this._particles) {
      const col = Math.round(particle.x);
      const row = Math.round(particle.y);

      if (particle.x >= 0 && particle.y >= 0 && col < w && row < h) {
        buffer.set(row, col, particle.glyph, this.template.fg, this.template.bg);
      }
    }
  }

  /** Whether the emitter has no living particles. */
  isEmpty(): boolean {
    return this._particles.length === 0;
  }

  /** The number of living particles. */
  particleCount(): number {
    return this._particles.length;
  }

  /** Set whether the emitter is actively spawning new particles. */
  setActive(active: boolean): void {
    this._active = active;
  }

  /** Whether the emitter is actively spawning. */
  isActive(): boolean {
    return this._active;
  }

  /** Get a read-only copy of all living particles. */
  particles(): readonly Particle[] {
    return this._particles;
  }
}
